import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2, Clock, Package, FlaskConical, Sparkles, MapPin, Phone, ArrowRight, AlertCircle, Search,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { BackToPortfolioButton } from "@/components/BackToPortfolioButton";
import RequestQuoteButton from "@/components/RequestQuoteButton";
import api from "@/lib/api";
import { formatDateAU } from "@/lib/formatDateAU";
import { useSEO, SEO_DEFAULTS } from "@/lib/useSEO";
import { CONTACT } from "@/lib/siteData";

const TIMELINE = [
  { key: "paid",       label: "Booking confirmed",  desc: "Payment received — collect your sample.", icon: CheckCircle2 },
  { key: "received",   label: "Sample received",    desc: "Your sample has arrived at our Noosaville lab.", icon: Package },
  { key: "processing", label: "Lab testing",        desc: "Our Lab Techs are analysing your sample.", icon: FlaskConical },
  { key: "completed",  label: "Results ready",      desc: "Your COA + plain-English summary are on their way.", icon: Sparkles },
];

// Map booking status → index of the *currently active* timeline step.
// done   = i <  currentIdx
// active = i === currentIdx
// pending = i >  currentIdx
const STATUS_TO_STEP = {
  pending: 0,      // sit on step 0 (Booking confirmed) but show as "Awaiting payment"
  paid: 0,
  received: 1,
  processing: 2,
  completed: 4,    // beyond the last index → all 4 steps render as COMPLETE
};

function statusIndex(s) {
  return STATUS_TO_STEP[s] ?? 0;
}

export default function Track() {
  const { reference } = useParams();
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useSEO({
    title: reference ? `Track booking ${reference}` : "Track your sample",
    description: "Track the status of your Aware Water Testing sample — from received to results.",
    url: `${SEO_DEFAULTS.baseUrl}/track${reference ? `/${reference}` : ""}`,
  });

  useEffect(() => {
    if (!reference) { setData(null); return; }
    setLoading(true); setError(null);
    api.get(`/track/${reference}`)
      .then(({ data }) => setData(data))
      .catch((e) => {
        if (e.response?.status === 404) setError("We couldn't find a booking with that reference. Please check and try again.");
        else setError("Something went wrong. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [reference]);

  const submit = (e) => {
    e.preventDefault();
    const ref = input.trim().toUpperCase();
    if (!ref) return;
    navigate(`/track/${ref}`);
  };

  const cancelled = data?.status === "cancelled";
  const currentIdx = data ? statusIndex(data.status) : -1;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-28 lg:pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* PM-only — renders nothing for retail customers */}
          <div className="mb-6">
            <BackToPortfolioButton />
          </div>
          {/* Title */}
          <div className="text-center">
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-600">Sample Tracking</span>
            <h1 className="font-display mt-2 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              Track your <span className="water-text">water sample</span>
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-xl mx-auto">
              Enter your booking reference (starts with <span className="font-mono font-semibold text-slate-900">SCWT-</span>) to see live status.
            </p>
          </div>

          {/* Search */}
          <form onSubmit={submit} className="mt-10 max-w-xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                data-testid="track-input-reference"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="SCWT-XXXXXX"
                className="h-14 pl-12 pr-32 rounded-full text-base font-mono uppercase tracking-wider"
              />
              <Button
                data-testid="track-submit"
                type="submit"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 btn-aqua rounded-full font-semibold px-5 h-11"
              >
                Track <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </form>

          {/* Result */}
          {reference && (
            <div className="mt-12">
              {loading && (
                <div className="text-center text-slate-500 py-8">
                  <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                  Looking up <span className="font-mono font-semibold">{reference}</span>...
                </div>
              )}

              {error && (
                <div data-testid="track-error" className="mx-auto max-w-xl text-center bg-amber-50 border border-amber-200 rounded-2xl p-6">
                  <AlertCircle className="w-7 h-7 text-amber-600 mx-auto mb-3" />
                  <p className="text-amber-900 font-medium">{error}</p>
                  <p className="text-xs text-amber-700 mt-2">If you&apos;ve lost your reference, check the email we sent when you booked.</p>
                </div>
              )}

              {data && (
                <div data-testid="track-result" className="space-y-8">
                  {/* Header card */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="rounded-3xl bg-slate-900 text-white p-8 lg:p-10 relative overflow-hidden"
                  >
                    <div className="absolute -top-32 -right-32 w-80 h-80 bg-cyan-500/25 rounded-full blur-3xl" />
                    <div className="relative grid sm:grid-cols-2 gap-6">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-cyan-400 font-bold">Booking reference</div>
                        <div data-testid="track-reference" className="font-display text-3xl font-bold mt-1.5 tracking-wide">{data.reference}</div>
                      </div>
                      <div className="sm:text-right">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-cyan-400 font-bold">Current status</div>
                        <div data-testid="track-status" className={`inline-block mt-1.5 px-4 py-1.5 rounded-full text-sm font-bold capitalize ${cancelled ? "bg-rose-500/20 text-rose-300" : "bg-cyan-500/20 text-cyan-300"}`}>
                          {data.status === "pending" ? "Awaiting payment" : data.status}
                        </div>
                      </div>
                    </div>
                    <div className="relative mt-6 pt-6 border-t border-white/10 grid sm:grid-cols-3 gap-4 text-sm">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Customer</div>
                        <div className="text-white font-medium mt-0.5">{data.customer_first_name}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Test</div>
                        <div className="text-white font-medium mt-0.5">{data.test_name}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Booked</div>
                        <div className="text-white font-medium mt-0.5">{formatDateAU(data.created_at)}</div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Timeline */}
                  {!cancelled ? (
                    <div className="bg-white rounded-3xl border border-slate-200 p-7 lg:p-10">
                      <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600 mb-6">Progress</div>
                      <div className="relative">
                        <div className="absolute left-[27px] top-2 bottom-2 w-px bg-slate-200" />
                        <div className="space-y-6">
                          {TIMELINE.map((step, i) => {
                            const done = i < currentIdx;
                            const isCurrent = i === currentIdx && data.status !== "pending";
                            const isAwaiting = i === 0 && data.status === "pending";
                            const Icon = step.icon;
                            return (
                              <div key={step.key} className="relative flex items-start gap-5">
                                <div className={`relative z-10 w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 transition-all ${
                                  done
                                    ? "bg-cyan-500 text-white shadow-lg shadow-cyan-500/30"
                                    : isCurrent || isAwaiting
                                      ? "bg-white border-2 border-cyan-500 text-cyan-600"
                                      : "bg-white border border-slate-200 text-slate-400"
                                }`}>
                                  <Icon className="w-5 h-5" />
                                  {(isCurrent || isAwaiting) && (
                                    <span className="absolute inset-0 rounded-2xl border-2 border-cyan-400 animate-ping opacity-50" />
                                  )}
                                </div>
                                <div className="flex-1 pt-1.5">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className={`font-display font-semibold text-base ${done || isCurrent || isAwaiting ? "text-slate-900" : "text-slate-400"}`}>{step.label}</h3>
                                    {done && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-wider">
                                        <CheckCircle2 className="w-3 h-3" /> Complete
                                      </span>
                                    )}
                                    {isCurrent && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 text-[10px] font-bold uppercase tracking-wider">
                                        <Clock className="w-3 h-3" /> In progress
                                      </span>
                                    )}
                                    {isAwaiting && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-bold uppercase tracking-wider">
                                        <Clock className="w-3 h-3" /> Awaiting payment
                                      </span>
                                    )}
                                  </div>
                                  <p className={`mt-1 text-sm ${done || isCurrent || isAwaiting ? "text-slate-600" : "text-slate-400"}`}>{step.desc}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {data.status === "pending" && (
                        <div className="mt-7 p-4 rounded-2xl bg-amber-50 border border-amber-100">
                          {data.pm_booking || data.payment_terms === "30_days" ? (
                            <p
                              data-testid="track-pending-pm-net30"
                              className="text-sm text-amber-900"
                            >
                              <strong>Bank transfer pending</strong>
                              {data.payment_due_at ? (
                                <> — your invoice is due <span className="font-semibold">{formatDateAU(data.payment_due_at)}</span>.</>
                              ) : (
                                <> — your invoice will be paid via your agency account.</>
                              )}
                            </p>
                          ) : (
                            <p
                              data-testid="track-pending-retail"
                              className="text-sm text-amber-900"
                            >
                              <strong>Payment incomplete.</strong> Complete checkout to lock in your booking and 20% discount.
                            </p>
                          )}
                        </div>
                      )}
                      {data.status === "completed" && (
                        <div className="mt-7 p-4 rounded-2xl bg-emerald-50 border border-emerald-100">
                          <p className="text-sm text-emerald-900">
                            <strong>Your results are ready.</strong> Check your email for the Certificate of Analysis and plain-English summary.
                          </p>
                          <div className="mt-4 flex flex-wrap items-center gap-3">
                            <RequestQuoteButton
                              bookingRef={data.reference}
                              authenticated={false}
                              variant="primary"
                              dataTestId="track-request-quote-btn"
                            />
                            <span className="text-xs text-emerald-900/80">
                              Recommended treatment on your report? Ask your local Pristine Water Systems specialist for their own quotation.
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div data-testid="track-cancelled" className="bg-rose-50 border border-rose-100 rounded-3xl p-7 text-center">
                      <AlertCircle className="w-7 h-7 text-rose-600 mx-auto mb-3" />
                      <h3 className="font-display font-semibold text-rose-900 text-lg">This booking was cancelled</h3>
                      <p className="text-sm text-rose-700 mt-1">Contact us if this looks incorrect.</p>
                    </div>
                  )}

                  {/* Help block */}
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="rounded-2xl bg-slate-50 border border-slate-100 p-5">
                      <MapPin className="w-5 h-5 text-cyan-500 mb-2" />
                      <div className="font-semibold text-slate-900 text-sm">Drop your sample at the lab</div>
                      <div className="text-xs text-slate-500 mt-0.5">{CONTACT.address}</div>
                    </div>
                    <a href={CONTACT.phoneLink} className="rounded-2xl bg-slate-50 border border-slate-100 p-5 hover:border-cyan-300 transition-colors block">
                      <Phone className="w-5 h-5 text-cyan-500 mb-2" />
                      <div className="font-semibold text-slate-900 text-sm">Questions? Call the lab</div>
                      <div className="text-xs text-slate-500 mt-0.5">{CONTACT.phone} — Mon–Fri</div>
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}

          {!reference && (
            <div className="mt-14 max-w-xl mx-auto text-center text-sm text-slate-500">
              <p>Don&apos;t have a reference? <Link to="/book" className="font-semibold text-cyan-600 hover:underline">Book a test →</Link></p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
